import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MacroText from 'components/MacroWizard/MacroText/MacroText';
import AgentAssistSource from './AgentAssistSource/AgentAssistSource';
import {
  extractIncomingMessagesArray,
  extractOutgoingMessagesArray,
  extractSelectedTicketFromState,
  getConversationMoreTicketInfo,
} from 'extractFromState';
import ButtonList from 'components/DecisionTree/NodeGroups/DTQuestionOrInstruction/Common/ButtonList/ButtonList';
import Loading from 'components/LoadingComponent/LoadingComponent';
import { LoadingWrapper } from 'components/MacroWizard/MWResultPage/MWResultPage.styles';
import { getAgentAssistMacro } from 'actions/tools/CommonMLActions';
import { returnToPreviousMacroWizardPath, addEdgeToDecisionTreePath } from 'actions/searchActions';
import { handleError } from 'services/ErrorService/ErrorService';
import {
  Container,
  TitleText,
  SubText,
  PageHeader,
  MacroContentSection,
  ValidateSection,
  ValidateText,
} from './DTMLAgentAssistNode.styles';
import {
  getSimplrAccountIdOfSelectedTicket,
  getSelectedTicketSubject,
  getselectedConversationId,
  getSelectedTicketCustomerEmail,
  getSelectedTicketBrand,
  getSelectedTicketCommunicationType,
  getCurrentDecisionNodeContent,
  extractCurrentDecisionTreePathArray,
  extractEasyConnectionDataForSelectedTicket,
  isLoadingUnique,
  extractAgentAssistMacro,
  extractAgentAssistSource,
} from 'extractFromState';

const NO_AGENT_ASSIST_RESPONSE_EDGE = 'no';

const DTMLAgentAssistNode = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => isLoadingUnique(state, 'mlAssistPage'));
  const content = useSelector(getCurrentDecisionNodeContent);
  const ticketSubject = useSelector(getSelectedTicketSubject);
  const simplrAccountId = useSelector(getSimplrAccountIdOfSelectedTicket);
  const conversationId = useSelector(getselectedConversationId);
  const asinId = useSelector(getConversationMoreTicketInfo)?.asin;
  const initialContactId = useSelector(getConversationMoreTicketInfo)?.initialContactId;
  const productCategory = useSelector(getConversationMoreTicketInfo)?.productCategory;
  const productLink = useSelector(getConversationMoreTicketInfo)?.productLink;

  const channel = useSelector(getSelectedTicketCommunicationType);
  const brand = useSelector(getSelectedTicketBrand);
  const customerEmail = useSelector(getSelectedTicketCustomerEmail);
  const decisionTreeContext = useSelector(extractCurrentDecisionTreePathArray);
  const incomingMessages = useSelector(extractIncomingMessagesArray);
  const outgoingMessages = useSelector(extractOutgoingMessagesArray);
  const easyConnectionData = useSelector(extractEasyConnectionDataForSelectedTicket);
  const editableMacroContent = useSelector(extractAgentAssistMacro);
  const sources = useSelector(extractAgentAssistSource);
  const simplrTicketNumber = useSelector(extractSelectedTicketFromState)?.simplrTicketNumber;

  useEffect(() => {
    (async () => {
      const params = {
        subject: ticketSubject,
        messageBody: incomingMessages,
        simplrAccountId,
        conversationId,
        asin_id: asinId,
        initialContactId,
        productCategory,
        productLink,
        channel,
        brand: brand ?? 'None',
        customerEmail,
        decisionTreeContext,
        simplrTicketNumber,
        outgoingMessages,
      };

      try {
        const agentAssistMacro = await dispatch(getAgentAssistMacro(params));

        if (!agentAssistMacro?.isAnswerPresent) {
          dispatch(addEdgeToDecisionTreePath(NO_AGENT_ASSIST_RESPONSE_EDGE));
        }
      } catch (error) {
        handleError({ error, customMessage: error.message, showPopup: true });
        dispatch(returnToPreviousMacroWizardPath());
      }
    })();
  }, [
    dispatch,
    brand,
    channel,
    asinId,
    initialContactId,
    productCategory,
    productLink,
    conversationId,
    customerEmail,
    decisionTreeContext,
    simplrAccountId,
    ticketSubject,
    incomingMessages,
    outgoingMessages,
    simplrTicketNumber,
  ]);

  return isLoading ? (
    <LoadingWrapper>
      <Loading label={'Loading -- This could take a minute while our Expert Assistant searches for a response...'} />
    </LoadingWrapper>
  ) : (
    <>
      <Container>
        <PageHeader>
          <img alt="warning-icon" src="/images/warning-circle.svg" />
          <TitleText>{content.title}</TitleText>
          <SubText>{content.subText}</SubText>
        </PageHeader>
        <MacroContentSection>
          <MacroText
            easyConnectionData={easyConnectionData}
            editableMacroContent={editableMacroContent ?? ''}
            onReplaceTrigger={() => null}
          />
        </MacroContentSection>
        {sources && <AgentAssistSource sources={sources} />}
        <ValidateSection>
          <ValidateText>Is this answer accurate and addresses the customer's question?</ValidateText>
          <ButtonList isButtonDisabled={false} />
        </ValidateSection>
      </Container>
    </>
  );
};

export default DTMLAgentAssistNode;
